import React, { useState, useRef } from 'react';

export interface GalleryImage {
  id: string;
  url: string;
  title?: string;
  date?: string;
  thumbnailUrl?: string;
}

export interface GridGalleryProps {
  images: GalleryImage[];
  onUpload?: (file: File) => void;
  onDelete?: (id: string) => void;
  title?: string;
}

export default function GridGallery({ images, onUpload, onDelete, title = "Clinical Gallery" }: GridGalleryProps) {
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && onUpload) {
      onUpload(file);
    }
  };

  return (
    <div className="apexo-gallery-wrapper">
      <div className="apexo-gallery-header">
        <h4 className="apexo-gallery-title">{title}</h4>
        {onUpload && (
          <>
            <input 
              type="file" 
              ref={fileInputRef} 
              style={{ display: 'none' }} 
              accept="image/*"
              onChange={handleFileChange}
            />
            <button 
              className="apexo-gallery-upload-btn"
              onClick={() => fileInputRef.current?.click()}
            >
              + Upload
            </button>
          </>
        )}
      </div>

      <div className="apexo-gallery-grid">
        {images.map(img => (
          <div key={img.id} className="apexo-gallery-card">
            <div 
              className="apexo-gallery-img-container"
              onClick={() => setSelectedImage(img)}
            >
              <img 
                src={img.thumbnailUrl || img.url} 
                alt={img.title || 'Clinical Image'} 
                className="apexo-gallery-img"
              />
              <div className="apexo-gallery-overlay">
                <span className="apexo-gallery-view-icon">👁️</span>
              </div>
            </div>
            <div className="apexo-gallery-footer">
              <div className="apexo-gallery-info">
                <span className="apexo-gallery-img-title">{img.title || 'Untitled'}</span>
                {img.date && <span className="apexo-gallery-date">{img.date}</span>}
              </div>
              {onDelete && (
                <button 
                  className="apexo-gallery-delete-btn"
                  onClick={(e) => { e.stopPropagation(); onDelete(img.id); }}
                  title="Delete Image"
                >
                  🗑️
                </button>
              )}
            </div>
          </div>
        ))}
        {images.length === 0 && (
          <div className="apexo-gallery-empty">
            No images in gallery.
          </div>
        )}
      </div>

      {selectedImage && (
        <div className="apexo-gallery-lightbox" onClick={() => setSelectedImage(null)}>
          <div className="apexo-gallery-lightbox-content" onClick={e => e.stopPropagation()}>
            <button className="apexo-gallery-close" onClick={() => setSelectedImage(null)}>✕</button>
            <img 
              src={selectedImage.url} 
              alt={selectedImage.title || 'Clinical Image'} 
              className="apexo-gallery-lightbox-img"
            />
            <div className="apexo-gallery-lightbox-caption">
              {selectedImage.title || 'Untitled'}
              {selectedImage.date && ` • ${selectedImage.date}`}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
